// Deal economics step (stories 27-29): ACV and win rate once, and current annual pipeline only when a
// selected use case reads it. (The Clay contract estimate lives on the ROI summary.)
//
// Re-render-safe draft state (root CLAUDE.md "Draft State Under SSE"): the inputs edit a
// `useDraftState` text draft seeded once per store `epoch`. An unrelated re-render never resets
// what the SE is typing; a `hydrate` (new epoch) reseeds. Every keystroke also pushes the parsed
// number into the store, which stays the single source of truth for the engine.

import { AUTOFILL_IGNORE_PROPS } from '../../../_shared/autofill';
import { useDraftState } from '../../../_shared/hooks';
import { getSizedUseCase, DEFAULT_ACV, DEFAULT_CURRENT_ANNUAL_PIPELINE, DEFAULT_WIN_RATE } from '../../data/use-case-catalog';
import { casesRequiringCurrentPipeline } from '../../engine/use-case-engine';
import { numberToInput, parseNumberInput } from '../../flow/number-input';
import { NumberField } from './NumberField';
import { calculatorActions, useCalculatorStore } from '../../state/calculator-store';

interface DealDraft {
  acv: string;
  winRate: string;
  currentAnnualPipeline: string;
}

type DealField = keyof DealDraft;

/**
 * The draft-backed deal inputs, shared by this step and the review table so both edit through one
 * re-render-safe draft. `onChange` updates the text draft and pushes the parsed number to the store.
 */
export function useDealDraft() {
  const epoch = useCalculatorStore((s) => s.epoch);
  const deal = useCalculatorStore((s) => s.deal);
  const seed: DealDraft = {
    acv: numberToInput(deal.acv),
    winRate: numberToInput(deal.winRate),
    currentAnnualPipeline: numberToInput(deal.currentAnnualPipeline),
  };
  const { draft, setDraft } = useDraftState<DealDraft>(seed, epoch);
  const onChange = (field: DealField, text: string) => {
    setDraft({ [field]: text });
    const n = parseNumberInput(text);
    if (field === 'acv') calculatorActions.setDeal({ acv: n ?? 0 });
    else if (field === 'winRate') calculatorActions.setDeal({ winRate: Math.min(n ?? 0, 100) });
    // Blank restores the default (so Assumed always means "still the default"), as on the review table.
    else calculatorActions.setDeal({ currentAnnualPipeline: n ?? DEFAULT_CURRENT_ANNUAL_PIPELINE });
  };
  return { d: draft ?? seed, deal, onChange };
}

/** The deal economics inputs, without the step's heading. */
export function DealEconomicsForm() {
  const { d, deal, onChange } = useDealDraft();
  const selected = useCalculatorStore((s) => s.selected);

  const pipelineFor = casesRequiringCurrentPipeline(selected.map((s) => s.id)).map(
    (id) => getSizedUseCase(id)?.name ?? id,
  );
  const needsPipeline = pipelineFor.length > 0;
  const pipelineMissing = needsPipeline && !(Number(deal.currentAnnualPipeline) > 0);
  const money = (n: number) => `$${n.toLocaleString('en-US')}`;
  const winRateInvalid = d.winRate !== '' && (parseNumberInput(d.winRate) ?? 101) > 100;

  return (
    <div className="vc3-form">
      <div className="vc3-dcards" data-count={needsPipeline ? 3 : 2}>
        <div className="vc3-dcard">
          <label className="vc3-dcard-label" htmlFor="vc3-acv">
            Average deal size (ACV)
          </label>
          <div className="vc3-dcard-field">
            <span aria-hidden>$</span>
            <NumberField
              id="vc3-acv"
              className="vc3-dcard-input"
              {...AUTOFILL_IGNORE_PROPS}
              value={d.acv}
              onChange={(t) => onChange('acv', t)}
            />
          </div>
          <span className="vc3-dcard-help">
            Default {`$${DEFAULT_ACV.toLocaleString('en-US')}`}.{' '}
            {deal.acv !== DEFAULT_ACV && (
              <button
                type="button"
                className="vc3-linkbtn"
                onClick={() => onChange('acv', numberToInput(DEFAULT_ACV))}
              >
                Use default
              </button>
            )}
          </span>
        </div>

        <div className="vc3-dcard">
          <label className="vc3-dcard-label" htmlFor="vc3-win">
            Win rate
          </label>
          <div className="vc3-dcard-field">
            <NumberField
              id="vc3-win"
              className="vc3-dcard-input"
              {...AUTOFILL_IGNORE_PROPS}
              aria-invalid={winRateInvalid}
              aria-describedby="vc3-win-help"
              value={d.winRate}
              onChange={(t) => onChange('winRate', t)}
            />
            <span aria-hidden>%</span>
          </div>
          <span id="vc3-win-help" className="vc3-dcard-help">
            {winRateInvalid ? 'Win rate can be at most 100%.' : `By dollar value, not deal count. Default ${DEFAULT_WIN_RATE}%. `}
            {!winRateInvalid && deal.winRate !== DEFAULT_WIN_RATE && (
              <button
                type="button"
                className="vc3-linkbtn"
                onClick={() => onChange('winRate', String(DEFAULT_WIN_RATE))}
              >
                Use default
              </button>
            )}
          </span>
        </div>
        {needsPipeline && (
          <div className="vc3-dcard">
            <label className="vc3-dcard-label" htmlFor="vc3-pipeline">
              Current annual pipeline
            </label>
            <div className="vc3-dcard-field">
              <span aria-hidden>$</span>
              <NumberField
                id="vc3-pipeline"
                className="vc3-dcard-input"
                {...AUTOFILL_IGNORE_PROPS}
                aria-invalid={pipelineMissing}
                aria-describedby="vc3-pipeline-help"
                value={d.currentAnnualPipeline}
                onChange={(t) => onChange('currentAnnualPipeline', t)}
              />
            </div>
            <span id="vc3-pipeline-help" className="vc3-dcard-help">
              Used by {pipelineFor.join(' and ')}: the pipeline created in a year today, before Clay. Default{' '}
              {money(DEFAULT_CURRENT_ANNUAL_PIPELINE)}.{' '}
              {deal.currentAnnualPipeline !== DEFAULT_CURRENT_ANNUAL_PIPELINE && (
                <button
                  type="button"
                  className="vc3-linkbtn"
                  onClick={() => onChange('currentAnnualPipeline', numberToInput(DEFAULT_CURRENT_ANNUAL_PIPELINE))}
                >
                  Use default
                </button>
              )}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

export function DealEconomicsStep() {
  return (
    <section aria-labelledby="vc3-deal-h">
      <div className="vc3-eyebrow">Step 3</div>
      <h1 id="vc3-deal-h" className="vc3-h1">
        Deal economics
      </h1>
      <p className="vc3-lede">
        Entered once and reused by every use case. Your own numbers always replace the defaults.
      </p>
      <DealEconomicsForm />
    </section>
  );
}
